
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { WorkoutType } from "@/models/WorkoutType";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export const useWorkoutTypeMutations = () => {
  const { currentUserRole, profile, currentOrganization } = useAuth();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const canManage = currentUserRole === 'admin' || currentUserRole === 'manager';

  // Create workout type mutation
  const createWorkoutTypeMutation = useMutation({
    mutationFn: async (workoutTypeData: Partial<WorkoutType>) => {
      if (!canManage) {
        throw new Error(t('workoutTypes.cannotManage'));
      }

      if (!currentOrganization || !profile) {
        throw new Error('Missing organization or profile');
      }

      const { data, error } = await supabase
        .from('workout_types')
        .insert({
          name: workoutTypeData.name,
          description: workoutTypeData.description,
          color: workoutTypeData.color,
          is_active: workoutTypeData.is_active ?? true,
          organization_id: currentOrganization.id,
          created_by: profile.user_id
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating workout type:', error);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workoutTypes', currentOrganization?.id] });
      toast.success(t('workoutTypes.createSuccess'));
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : t('workoutTypes.errors.createFailed');
      toast.error(errorMessage);
    },
  });

  // Update workout type mutation
  const updateWorkoutTypeMutation = useMutation({
    mutationFn: async ({ id, workoutTypeData }: { id: string; workoutTypeData: Partial<WorkoutType> }) => {
      if (!canManage) {
        throw new Error(t('workoutTypes.cannotManage'));
      }

      if (!profile) {
        throw new Error('Missing profile');
      }

      const { data, error } = await supabase
        .from('workout_types')
        .update({
          name: workoutTypeData.name,
          description: workoutTypeData.description,
          color: workoutTypeData.color,
          is_active: workoutTypeData.is_active,
          updated_by: profile.user_id
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating workout type:', error);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workoutTypes', currentOrganization?.id] });
      toast.success(t('workoutTypes.updateSuccess'));
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : t('workoutTypes.errors.updateFailed');
      toast.error(errorMessage);
    },
  });

  // Delete workout type mutation
  const deleteWorkoutTypeMutation = useMutation({
    mutationFn: async ({ id, checkCanDelete }: { id: string; checkCanDelete: (id: string) => Promise<any> }) => {
      if (!canManage) {
        throw new Error(t('workoutTypes.cannotManage'));
      }

      // First check if the workout type can be deleted
      const deleteCheck = await checkCanDelete(id);

      if (!deleteCheck) {
        throw new Error('Failed to check if workout type can be deleted');
      }

      if (!deleteCheck.can_delete) {
        throw new Error(deleteCheck.reason);
      }

      const { error } = await supabase
        .from('workout_types')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting workout type:', error);
        throw error;
      }

      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workoutTypes', currentOrganization?.id] });
      toast.success(t('workoutTypes.deleteSuccess'));
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : t('workoutTypes.errors.deleteFailed');
      toast.error(errorMessage);
    },
  });

  return {
    canManage,
    createWorkoutTypeMutation,
    updateWorkoutTypeMutation,
    deleteWorkoutTypeMutation,
  };
};
